import * as React from 'react';
import { useMemo } from 'react';
import parse, { domToReact, DOMNode } from 'html-react-parser'
import { Citation, Footnote } from './types';
import InlineSources from './InlineSources';
import { MailOutlined, LinkOutlined } from "@ant-design/icons";
import { Popover } from 'antd';
import { Typography } from 'antd';

const { Title, Paragraph, Text, Link } = Typography;

interface Props {
    text: string[];
}

const HtmlTextRenderer: React.FC<Props> = ({
    text,
}) => {
    const options = useMemo(
        () => ({
            replace(domNode: DOMNode) {
                if (domNode.type === 'tag') {
                    const children = domToReact(
                        domNode.children as DOMNode[],
                        options,
                    );

                    if (domNode.name === 'h1') {
                        return (
                            <Title level={1}>
                                {children}
                            </Title>
                        );
                    }
                    if (domNode.name === 'h2') {
                        return (
                            <Title level={2}>
                                {children}
                            </Title>
                        );
                    }
                    if (domNode.name === 'p') {
                        return (
                            <Paragraph style={{ marginBottom: '0.5rem' }}>
                                {children}
                            </Paragraph>
                        );
                    }
                    // Was not able to use a custom HTML element due to behavior of html-react-parser such as placing
                    // a custom element inside a p element causing the custom element to not be detected by the parser
                    if (domNode.name === 'span') {
                        if (domNode.attribs['type'] === 'sources-container') {
                            const { citations, footnotes } = JSON.parse(
                                domNode.attribs['data'],
                            ) as {
                                citations: Citation[];
                                footnotes: Footnote[];
                                id: string;
                            };

                            return (
                                <InlineSources
                                    citations={citations}
                                    footnotes={footnotes}
                                />
                            );
                        }
                    }
                    if (domNode.name === 'a') {
                        const href = domNode.attribs['href'];
                        const mailto = href.startsWith('mailto:');
                        const target = domNode.attribs['target'] ?? null;

                        return (
                            <Link
                                href={href}
                                target='_blank'
                                rel='noreferrer'
                                style={{ color: 'black', textDecoration: 'underline' }}
                            >
                                
                                {children}
                                {mailto && <MailOutlined style={{ marginLeft: '3px' }} />}
                                {target && <LinkOutlined style={{ marginLeft: '3px' }} />}
                            </Link>
                        );
                    }
                    if (domNode.name === 'abbr') {
                        const title = domNode.attribs['title'];

                        return (
                            <Popover content={title}>
                                <Text style={{ textDecoration: 'underline', textDecorationStyle: 'dotted', cursor: 'help' }}>{children}</Text>
                            </Popover>
                        );
                    }
                }
            },
        }),
        [],
    );

    return <div>{parse(text.join(''), options)}</div>;
};

export default React.memo(HtmlTextRenderer);
